// @ts-strict-ignore
import * as React from 'react';
import {clsx} from 'clsx';

import {useTranslation} from 'common/i18n/hooks';
import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {TooltipReact} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {TrackingUsageContext} from 'common/report/tracking-setup';
import {getCurrentMoment} from 'common/util/date';
import {DateRange, DateRangeAttributes} from 'common/widgets/date-range-picker/models/date-range';
import {PropertyFilterDateRange} from 'common/widgets/date-range-picker/models/property-filter-date-range';
import {DateRangeType, DisplayContext} from 'common/widgets/date-range-picker/models/types';
import {DateRangePickerReact} from 'common/widgets/date-range-picker-new';
import {getDateRangeLabel} from 'common/widgets/date-range-picker-new/util';
import {getDateRangeTooltipRenderer} from 'common/widgets/date-range-preset-bar/date-range-tooltip';

import {FilterValueProps} from '../types';

export function FilterDateValue(props: FilterValueProps) {
  const currentProject = useCurrentProject().get().value;
  const {t, print} = useTranslation();
  /**
   * All possible date filters are expressed as a relative or absolute date selection
   * we express date filters in terms of these 2 primitives
   */
  const selectedDateAttrs = new DateRange(props.filter.filterValue as DateRangeAttributes).getAttributes();
  switch (selectedDateAttrs.type) {
    case DateRangeType.NotOn:
      selectedDateAttrs.type = DateRangeType.On;
      break;
    case DateRangeType.NotBetween:
      selectedDateAttrs.type = DateRangeType.Between;
      break;
    case DateRangeType.RelativeFuture:
    case DateRangeType.RelativeBefore:
    case DateRangeType.NotRelativeAfter:
      selectedDateAttrs.type = DateRangeType.RelativeAfter;
      break;
    case DateRangeType.Before:
      selectedDateAttrs.type = DateRangeType.On;
      selectedDateAttrs.from = selectedDateAttrs.to;
      break;
  }
  const selectedDate = new PropertyFilterDateRange(selectedDateAttrs);
  props.filter.filterValue = selectedDate;

  /**
   * TODO: date filters can all be expressed with the following operators:
   *
   * ==, !=, >=, ∋, ∌, <=
   * = (in the last, on)
   * != (not in the last, not on)
   * > (in the next, since)
   * ∋ (between)
   * ∌ (not between)
   * < (before the last, ending)
   *
   * These require additional changes to the API:
   *  - [!= last x days] not in the last: translates to not between
   *  - [< date] before date: translates to "ending at date"
   */
  const handleDatePickerChange = (ev: CustomEvent) => {
    const dateRange: DateRange = ev.detail.dateRange;
    props.setTimePickerEnabled(ev.detail.isTimePickerEnabled);
    const {filterOperator} = props.filter;
    if (filterOperator === DateRangeType.Before) {
      dateRange.from = null;
    }

    const filterValue: PropertyFilterDateRange = props.filter.createDateRange({
      ...dateRange.getAttributes(),
      type: props.filter.filterOperator as DateRangeType,
    });

    const updatedFilter = props.filter.clonePropertyFilter({
      filterDateUnit: filterValue.window?.unit,
      filterValue,
    });

    props.updateEntry(updatedFilter);
  };

  return (
    <div className='filter-value-select-wrapper'>
      <SelectReact
        className={clsx(`filter-value-select`, {
          'is-placeholder': !props.filter.isValid(),
        })}
        elref='filterValueSelect'
        open={props.isActive}
        theme={props.hasNewUI ? SelectTheme.Tertiary : SelectTheme.Select}
        neverShowCaret
        readonly={props.readonly}
        selectedOption={
          props.filter.isValid()
            ? print(
                getDateRangeLabel({
                  dateRange: selectedDate,
                  matchingPreset: null,
                  projectAsOfDate:
                    null /* project as of date; we set this to "null" because we're trialing a change where we show the project-as-of-date in the tooltip, not the label */,
                  now: getCurrentMoment({localized: true}),
                  formatMode: `none`,
                }),
              )
            : t(`query-builder.value-placeholder`)
        }
        onMenuChange={(ev) => props.onSelectMenuChange(ev)}
      >
        {props.isActive && (
          <DateRangePickerReact
            elref='filterDateRangePicker'
            singlePickerMode
            isTimePickerEnabled={props.isTimePickerEnabled}
            displayContext={DisplayContext.PropertyFilter}
            slot='body'
            dateRange={props.filter.isValid() && selectedDate.getAttributes()}
            trackedUseCase={TrackingUsageContext.QueryBuilder}
            onApply={(ev) => handleDatePickerChange(ev)}
            onCancel={() => props.setActiveEntry(`close`)}
          />
        )}
      </SelectReact>
      {!props.isActive && props.filter.isValid() && (
        <TooltipReact
          elref='dateRangeTooltip'
          placement={Placement.Bottom}
          lazyContentRenderer={getDateRangeTooltipRenderer(props.filter.filterValue as DateRange, currentProject)}
        />
      )}
    </div>
  );
}
